import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { formatPrice } from "@/components/propertyDetail/helpers";
import { copyToClipboard } from "@/lib/clipboard";
import { CHROME_STORE_URL } from "@/lib/extensionConstants";
import {
  X, Copy, Check, ArrowSquareOut, Archive, Info,
} from "@phosphor-icons/react";

/**
 * PortalPublishAssistant · iter 312 — "Cheatsheet a fianco"
 * ──────────────────────────────────────────────────────────
 * Riscritto dopo feedback utente (17/05/2026): la versione "Copia & Avanti"
 * sequenziale era stressante e non copriva tutti i campi che i portali
 * chiedono davvero. L'agente diceva: "non funziona il gioco, sono disperato".
 *
 * Nuovo paradigma:
 *   - Pannello sticky bottom-right (o destro full-height) che l'utente
 *     tiene aperto a fianco della scheda del portale.
 *   - TUTTI i dati visibili insieme, raggruppati per sezione (Categoria,
 *     Posizione, Dati base, Caratteristiche, Prezzo, Testo, Foto).
 *   - Ogni riga ha un bottoncino "Copia" e ricorda quali sono già copiati.
 *   - Niente più "Avanti" automatico — l'utente va al ritmo che vuole.
 *   - Per i campi che vengono compilati via dropdown (Categoria, Arredamento,
 *     Riscaldamento) → mostriamo il valore con etichetta "Seleziona" invece
 *     di un bottone Copia, e una nota su come trovare l'opzione sul portale.
 *
 * NOTA onestà: il vero autofill arriva con l'estensione Chrome Pronto Publisher
 * (disponibile sullo Chrome Web Store). Questo cheatsheet è la migliore
 * esperienza possibile senza estensione (es. su mobile, Safari, Firefox).
 */
export default function PortalPublishAssistant({
  open,
  onClose,
  portalKey,
  portalName,
  portalUrl,
  property,
  pack,
  onDownloadZip,
  photoCount = 0,
}) {
  const [copied, setCopied] = useState({}); // { fieldKey: true }

  useEffect(() => {
    if (open) setCopied({});
  }, [open, portalKey]);

  // Split address → via + civico se possibile (regex semplice: numero finale)
  const { via, civico } = useMemo(() => {
    const addr = (property?.address || "").trim();
    if (!addr) return { via: "", civico: "" };
    const m = addr.match(/^(.*?)[,\s]+(\d+[/\-\w]*)\s*$/);
    if (m) return { via: m[1].trim(), civico: m[2].trim() };
    return { via: addr, civico: "" };
  }, [property?.address]);

  // Descrizione ottimizzata per il portale corrente
  const portalDesc =
    pack?.[portalKey] || pack?.[portalKey === "casait" ? "casa_it" : portalKey] || property?.ai_description || "";

  // Mappa categorie a stringhe leggibili per i portali (compilati via dropdown)
  const listingTypeLabel = property?.listing_type === "affitto" ? "Affitto" : "Vendita";
  const categoryHint = `Residenziale → ${listingTypeLabel} → ${property?.property_type || "Appartamento"}`;
  const furnishedMap = {
    "arredato": "Arredato",
    "semi-arredato": "Parzialmente arredato",
    "non-arredato": "Non arredato",
  };
  const furnishedLabel = furnishedMap[property?.furnished] || property?.furnished || "—";

  // Sezioni cheatsheet — solo campi presenti
  const sections = useMemo(() => {
    if (!property) return [];
    const sec = [];

    // 1. CATEGORIA (helper, niente copia)
    sec.push({
      title: "Categoria · seleziona sul portale",
      kind: "hint",
      rows: [
        { key: "category", label: "Tipologia", value: categoryHint, hint: "Sul portale: passi dropdown / scelta categoria" },
      ],
    });

    // 2. POSIZIONE
    sec.push({
      title: "Posizione",
      kind: "copy",
      rows: [
        property?.city          && { key: "city",     label: "Comune",     value: property.city },
        via                     && { key: "via",      label: "Indirizzo · via", value: via },
        civico                  && { key: "civico",   label: "Civico",     value: civico },
        property?.neighborhood  && { key: "neigh",    label: "Quartiere",  value: property.neighborhood },
      ].filter(Boolean),
    });

    // 3. DATI BASE
    sec.push({
      title: "Dati base",
      kind: "copy",
      rows: [
        property?.surface   && { key: "surface",   label: "Superficie · m²", value: String(property.surface) },
        property?.rooms     && { key: "rooms",     label: "Locali",      value: String(property.rooms) },
        property?.bathrooms && { key: "bathrooms", label: "Bagni",       value: String(property.bathrooms) },
        property?.floor     && { key: "floor",     label: "Piano",       value: String(property.floor) },
      ].filter(Boolean),
    });

    // 4. CARATTERISTICHE (mix dropdown + copia)
    const carRows = [];
    if (property?.furnished)    carRows.push({ key: "furnished",    label: "Arredamento",      value: furnishedLabel, dropdown: true, hint: "Sul portale: dropdown 'Arredato / Parzialmente / Non arredato'" });
    if (property?.heating)      carRows.push({ key: "heating",      label: "Riscaldamento",    value: property.heating, dropdown: true, hint: "Sul portale: dropdown 'Autonomo / Centralizzato / Pompa di calore'" });
    if (property?.energy_class) carRows.push({ key: "energy",       label: "Classe energetica", value: property.energy_class, dropdown: true, hint: "Sul portale: dropdown 'A4 / A3 / A / B / C / D / E / F / G'" });
    if (property?.year_built)   carRows.push({ key: "year_built",   label: "Anno costruzione", value: String(property.year_built) });
    if (property?.year_renovated) carRows.push({ key: "year_reno",  label: "Anno ristrutturazione", value: String(property.year_renovated) });
    if (property?.exposure)     carRows.push({ key: "exposure",     label: "Esposizione",      value: property.exposure, dropdown: true });
    if (Array.isArray(property?.features) && property.features.length > 0) {
      carRows.push({ key: "features", label: "Caratteristiche extra", value: property.features.join(", "), hint: "Sul portale: spunta i checkbox corrispondenti", multiline: true });
    }
    if (carRows.length > 0) sec.push({ title: "Caratteristiche", kind: "mixed", rows: carRows });

    // 5. PREZZO
    const priceRows = [];
    if (property?.price) {
      priceRows.push({
        key: "price",
        label: property.listing_type === "affitto" ? "Canone mensile · €" : "Prezzo · €",
        value: String(property.price),
        hint: "Solo cifra, senza € e senza punti",
      });
    }
    if (property?.monthly_fees) {
      priceRows.push({ key: "monthly_fees", label: "Spese condominiali · €/mese", value: String(property.monthly_fees) });
    }
    if (property?.listing_type === "affitto" && property?.deposit_months) {
      priceRows.push({ key: "deposit", label: "Cauzione · mensilità", value: String(property.deposit_months) });
    }
    if (property?.listing_type === "affitto" && property?.contract_type) {
      priceRows.push({ key: "contract", label: "Tipo contratto", value: property.contract_type, dropdown: true });
    }
    if (priceRows.length > 0) sec.push({ title: "Prezzo e costi", kind: "mixed", rows: priceRows });

    // 6. TESTO
    sec.push({
      title: "Testo annuncio",
      kind: "copy",
      rows: [
        property?.title && { key: "title", label: "Titolo", value: property.title },
        portalDesc      && { key: "description", label: `Descrizione · ottimizzata per ${portalName}`, value: portalDesc, multiline: true },
      ].filter(Boolean),
    });

    return sec.filter((s) => s.rows.length > 0);
  }, [property, via, civico, categoryHint, furnishedLabel, portalDesc, portalName]);

  const copy = async (row) => {
    // iter 313 r18 — copyToClipboard robusto (iOS Safari fallback).
    const r = await copyToClipboard(String(row.value), { shareTitle: row.label });
    if (r === "clipboard") {
      setCopied((c) => ({ ...c, [row.key]: true }));
      toast.success(`${row.label} copiato`, { duration: 1200 });
    } else if (r === "manual") {
      toast.error("Copia non riuscita");
    }
    // "share" → l'OS ha gestito
  };

  if (!open || !property) return null;

  // Conteggio progresso
  const allCopyRows = sections.flatMap((s) => s.rows.filter((r) => !r.dropdown && s.kind !== "hint"));
  const copiedCount = allCopyRows.filter((r) => copied[r.key]).length + (copied.photos ? 1 : 0);
  const totalCount = allCopyRows.length + 1; // +1 foto

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-[420px] max-w-[calc(100vw-2rem)] max-h-[88vh] bg-white rounded-2xl shadow-2xl border border-[#E5E7E0] flex flex-col overflow-hidden"
      data-testid="portal-publish-assistant"
      role="dialog"
      aria-label={`Cheatsheet pubblicazione ${portalName}`}
    >
      {/* HEADER */}
      <div className="px-5 py-3.5 bg-gradient-to-br from-[#7C8B6E] to-[#7C8B6E] text-white">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#A8B89A] mb-0.5">
              Cheatsheet · pubblica su
            </div>
            <h3 className="font-editorial text-lg leading-tight truncate">{portalName}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white -m-1 p-1 transition-colors"
            data-testid="assistant-close-btn"
            aria-label="Chiudi"
          >
            <X size={18} />
          </button>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          <a
            href={portalUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-[#A8B89A] hover:text-white transition-colors"
            data-testid="assistant-open-portal"
          >
            <ArrowSquareOut size={12} weight="bold" />
            Riapri scheda {portalName}
          </a>
          <span className="text-[11px] text-white/70" data-testid="assistant-progress">
            <strong className="text-white">{copiedCount}</strong>/{totalCount} copiati
          </span>
        </div>
      </div>

      {/* SUGGERIMENTO ALTO */}
      <div className="px-4 py-2 bg-[#F1F4ED] border-b border-[#EAE7DF] flex items-start gap-2">
        <Info size={13} weight="fill" className="text-[#7C8B6E] mt-0.5 flex-shrink-0" />
        <p className="text-[11.5px] text-[#5d6952] leading-snug">
          Tieni Pronto su mezzo schermo e {portalName} sull'altra metà. Clicca <strong>Copia</strong>,
          alterna scheda (Cmd/Alt+Tab), incolla. <strong>Vai al tuo ritmo.</strong>
        </p>
      </div>

      {/* BODY — sezioni */}
      <div className="flex-1 overflow-y-auto px-3 py-3 bg-[#FAFAF7] space-y-3" data-testid="assistant-sections">
        {sections.map((sec) => (
          <div key={sec.title} className="rounded-lg bg-white border border-[#EAE7DF] overflow-hidden">
            <div className="px-3 py-2 bg-[#FAFAF7] border-b border-[#EAE7DF]">
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6B6A65]">{sec.title}</span>
            </div>
            <ul className="divide-y divide-[#F1EFE9]">
              {sec.rows.map((row) => {
                const isCopied = !!copied[row.key];
                const isDropdown = !!row.dropdown;
                const isHint = sec.kind === "hint";
                return (
                  <li key={row.key} className="px-3 py-2.5 flex items-start gap-2.5" data-testid={`row-${row.key}`}>
                    <div className="flex-1 min-w-0">
                      <div className={`text-[11px] font-semibold leading-tight ${isCopied ? "text-[#7C8B6E]" : "text-[#1C1C1A]"}`}>
                        {row.label}
                      </div>
                      <div className={`text-[12.5px] mt-0.5 leading-snug text-[#1C1C1A] ${row.multiline ? "line-clamp-2" : "truncate"}`}>
                        {row.value}
                      </div>
                      {row.hint && (
                        <div className="text-[10.5px] text-[#9A968B] italic mt-0.5 leading-snug">
                          {row.hint}
                        </div>
                      )}
                    </div>
                    {isHint ? (
                      <span className="flex-shrink-0 text-[10px] uppercase tracking-[0.12em] font-bold text-[#9A968B] px-2 py-1 rounded-md bg-[#F1F4ED] whitespace-nowrap">
                        Manuale
                      </span>
                    ) : isDropdown ? (
                      <button
                        onClick={() => copy(row)}
                        className={`flex-shrink-0 inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-1.5 rounded-md transition-colors ${
                          isCopied
                            ? "bg-[#F1F4ED] text-[#7C8B6E] border border-[#7C8B6E]/30"
                            : "bg-white border border-[#E5E7E0] text-[#6B6A65] hover:border-[#7C8B6E]"
                        }`}
                        data-testid={`copy-${row.key}`}
                        title="Copia · poi selezionalo nel dropdown del portale"
                      >
                        {isCopied ? <Check size={10} weight="bold" /> : <Copy size={10} />}
                        Copia
                      </button>
                    ) : (
                      <button
                        onClick={() => copy(row)}
                        className={`flex-shrink-0 inline-flex items-center gap-1 text-[10.5px] font-semibold px-2.5 py-1.5 rounded-md transition-colors ${
                          isCopied
                            ? "bg-[#F1F4ED] text-[#7C8B6E] border border-[#7C8B6E]/30"
                            : "bg-[#1C1C1A] text-white hover:bg-black"
                        }`}
                        data-testid={`copy-${row.key}`}
                      >
                        {isCopied ? <Check size={10} weight="bold" /> : <Copy size={10} />}
                        {isCopied ? "Copiato" : "Copia"}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {/* FOTO — sezione finale */}
        <div className="rounded-lg bg-white border border-[#EAE7DF] overflow-hidden">
          <div className="px-3 py-2 bg-[#FAFAF7] border-b border-[#EAE7DF]">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6B6A65]">Foto</span>
          </div>
          <div className="px-3 py-2.5 flex items-start gap-2.5" data-testid="row-photos">
            <div className="flex-1 min-w-0">
              <div className={`text-[11px] font-semibold leading-tight ${copied.photos ? "text-[#7C8B6E]" : "text-[#1C1C1A]"}`}>
                ZIP foto migliorate · {photoCount}
              </div>
              <div className="text-[10.5px] text-[#9A968B] italic mt-0.5 leading-snug">
                Scarica · poi caricalo nella sezione foto del portale
              </div>
            </div>
            <button
              onClick={() => {
                onDownloadZip?.();
                setCopied((c) => ({ ...c, photos: true }));
                toast.success("ZIP scaricato");
              }}
              disabled={photoCount === 0}
              className={`flex-shrink-0 inline-flex items-center gap-1 text-[10.5px] font-semibold px-2.5 py-1.5 rounded-md transition-colors ${
                copied.photos
                  ? "bg-[#F1F4ED] text-[#7C8B6E] border border-[#7C8B6E]/30"
                  : "bg-[#1C1C1A] text-white hover:bg-black disabled:opacity-40"
              }`}
              data-testid="copy-photos"
            >
              {copied.photos ? <Check size={10} weight="bold" /> : <Archive size={10} weight="bold" />}
              {copied.photos ? "Scaricato" : "Scarica ZIP"}
            </button>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className="px-4 py-2 bg-white border-t border-[#EAE7DF] text-[10.5px] text-[#9A968B] leading-snug">
        Vuoi velocizzare? Installa <a href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer" className="text-[#7C8B6E] font-semibold hover:underline">Pronto Publisher per Chrome</a>: aiuta a compilare i campi e tiene i dati dell'annuncio sempre a destra durante la pubblicazione.
      </div>
    </div>
  );
}
